import { ORG_ROLE_NAMES, type OrgRoleName } from "./auth/permissions";

export interface OrgMembershipRoleDefinition {
  roleKey: OrgRoleName;
  label: string;
  summary: string;
  permissionBullets: readonly string[];
}

export const ORG_MEMBERSHIP_ROLES: readonly OrgMembershipRoleDefinition[] = [
  {
    roleKey: "owner",
    label: "Owner",
    summary: "Full app + org admin; manage members; all suite apps",
    permissionBullets: [
      "Full access to all applications and features",
      "Manage organization settings and configuration",
      "Invite, remove, and manage members",
      "Assign roles and permissions",
      "Manage reviews, contract types, and custom roles",
      "Access all data, reports, and administrative functions",
    ],
  },
  {
    roleKey: "admin",
    label: "Admin",
    summary: "Full app + org admin; manage members; all suite apps",
    permissionBullets: [
      "Full access to all applications and features",
      "Manage organization settings and configuration",
      "Invite, remove, and manage members",
      "Assign roles and permissions",
      "Manage reviews, contract types, and custom roles",
      "Access all data, reports, and administrative functions",
    ],
  },
  {
    roleKey: "manager",
    label: "Manager",
    summary: "Manage reviews/types/roles; Contract Review and KM; no org delete",
    permissionBullets: [
      "Full review management (create, view, update, archive)",
      "Create and manage Contract Types",
      "Create and manage Custom Roles",
      "Access reporting and workflow management",
      "Application scope: Contract Review, KM",
    ],
  },
  {
    roleKey: "member",
    label: "Member",
    summary: "Create/read/update/share reviews; read-only reference data",
    permissionBullets: [
      "Create, view, update, and share reviews",
      "Participate in assigned review workflows",
      "Access reference data in read-only mode",
      "Application scope: Contract Review, KM",
    ],
  },
  {
    roleKey: "viewer",
    label: "Viewer",
    summary: "Read-only reviews and reference data",
    permissionBullets: [
      "View reviews and review results",
      "Access reference data",
      "View reports and supporting information",
      "Application scope: Contract Review, KM",
    ],
  },
] as const;

export type EnabledOrgRoles = Record<OrgRoleName, boolean>;

export function getDefaultEnabledOrgRoles(): EnabledOrgRoles {
  return {
    owner: true,
    admin: true,
    manager: true,
    member: true,
    viewer: true,
  };
}

export function normalizeEnabledOrgRoles(
  input?: Partial<EnabledOrgRoles> | null
): EnabledOrgRoles {
  const defaults = getDefaultEnabledOrgRoles();
  if (!input) return defaults;

  const normalized: EnabledOrgRoles = {
    owner: true,
    admin: input.admin ?? defaults.admin,
    manager: input.manager ?? defaults.manager,
    member: input.member ?? defaults.member,
    viewer: input.viewer ?? defaults.viewer,
  };

  const nonOwnerEnabled = ORG_ROLE_NAMES.filter(
    (role) => role !== "owner" && normalized[role]
  );
  if (nonOwnerEnabled.length === 0) {
    throw new Error("At least one of admin, manager, member, or viewer must be enabled");
  }

  return normalized;
}

export function parseEnabledOrgRolesFromMetadata(
  metadata: Record<string, unknown> | null | undefined
): EnabledOrgRoles {
  const raw = metadata?.enabledOrgRoles;
  if (!raw || typeof raw !== "object") {
    return getDefaultEnabledOrgRoles();
  }
  return normalizeEnabledOrgRoles(raw as Partial<EnabledOrgRoles>);
}

export function isOrgRoleEnabled(
  enabledOrgRoles: EnabledOrgRoles,
  roleKey: OrgRoleName
): boolean {
  return enabledOrgRoles[roleKey] === true;
}

/** Roles that can be assigned when creating or updating members (excludes owner). */
export function getAssignableOrgRoleKeys(enabledOrgRoles: EnabledOrgRoles): OrgRoleName[] {
  return ORG_ROLE_NAMES.filter(
    (role) => role !== "owner" && isOrgRoleEnabled(enabledOrgRoles, role)
  );
}
